'use client';

import { Crop, RefreshCw, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { KagemaneVideoPreview } from './KagemaneVideoPreview';
import { KagemaneMaterials } from './KagemaneMaterials';
import { KAGEMANE_RESOLUTIONS, type KagemaneResolution } from '@/components/ui/KagemaneResolutionChoice';
import type { UserMaterial } from '@/lib/api/materials';
import { Select } from '@/components/ui/Select';
import { KagemaneAutoplayVideo } from './KagemaneAutoplayVideo';
import type { ReactNode } from 'react';
import styles from './KagemaneSetupFields.module.css';

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';
const panelClass = 'rounded-2xl border border-[#E8ECEF] bg-[#F4F6F7]';

export function KagemaneSetupFields({
  referenceName, referencePoster, referenceSrc, materials, maxMaterials, disabled, previewActive = true,
  onChangeReference, onEditReference, onAddMaterial, onRemoveMaterial, promptEnabled, onTogglePrompt,
  depthEnabled, showDepth = true, onToggleDepth,
  prompt, onPromptChange, promptLimit, promptTooLong, resolution, resolutionOptions = KAGEMANE_RESOLUTIONS, onResolutionChange, model, onModelChange, modelError, genjutsuFields, optionalPeople, compactMobile = false,
}: {
  referenceName: string;
  referencePoster?: string;
  referenceSrc?: string;
  materials: UserMaterial[];
  maxMaterials: number;
  disabled: boolean;
  previewActive?: boolean;
  onChangeReference: () => void;
  onEditReference?: () => void;
  onAddMaterial: () => void;
  onRemoveMaterial: (id: string) => void;
  depthEnabled: boolean;
  showDepth?: boolean;
  onToggleDepth: () => void;
  promptEnabled: boolean;
  onTogglePrompt: () => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  promptLimit: number;
  promptTooLong: boolean;
  resolution: KagemaneResolution;
  resolutionOptions?: readonly KagemaneResolution[];
  onResolutionChange: (value: KagemaneResolution) => void;
  model?: 'seedance' | 'heygen' | 'kling4' | 'genjutsu';
  onModelChange?: (value: 'seedance' | 'heygen' | 'kling4' | 'genjutsu') => void;
  modelError?: string;
  genjutsuFields?: ReactNode;
  optionalPeople?: boolean;
  compactMobile?: boolean;
}) {
  const t = useTranslations('kagemane');
  const editT = useTranslations('referenceVideoEditor');
  return <div className="space-y-2.5">
    <div className={`space-y-2.5 ${compactMobile ? 'hidden lg:block' : ''}`}>
    <div
      className={`${styles.referenceFrame} group relative mx-auto flex w-full items-center justify-center overflow-hidden rounded-2xl`}>
      <span className={`grid place-items-center overflow-hidden rounded-2xl border border-[#E8ECEF] bg-[#F4F6F7] ${referenceSrc ? 'max-w-full' : referencePoster ? 'w-full' : 'min-h-40 w-full p-5'}`}>
        {referenceSrc ? <KagemaneAutoplayVideo key={referenceSrc} src={referenceSrc} poster={referencePoster} label={referenceName} active={previewActive}
          className="rounded-2xl bg-[#E9EEF1]" />
          : referencePoster ? <KagemaneVideoPreview poster={referencePoster} className="min-h-32 w-full rounded-xl" mediaClassName="block h-32 w-full object-cover" />
            : <span className="flex min-h-32 flex-col items-center justify-center gap-3 text-[#667680]"><Video className="size-6" aria-hidden /><span className="text-sm">{t('referenceUploadPlaceholder')}</span></span>}
      </span>
      <button type="button" disabled={disabled} onClick={onChangeReference}
        aria-label={referenceSrc || referencePoster ? t('changeReferenceAria', { name: referenceName }) : t('referenceUploadPlaceholder')}
        className={`absolute ${referenceSrc || referencePoster ? `right-2 top-2 size-11 rounded-full ${focusClass}` : 'inset-0 rounded-2xl !bg-transparent !border-0 focus-visible:outline-none'} grid place-items-center border border-black/5 bg-white/95 text-[#53636D] transition-colors group-hover:text-[#176B89]`}>
        {(referenceSrc || referencePoster) && <RefreshCw className="size-3.5" aria-hidden />}
      </button>
    </div>

    {onEditReference && <button type="button" disabled={disabled} onClick={onEditReference}
      className={`flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#DCE8ED] bg-white text-sm text-[#176B89] hover:bg-[#F0FAFE] disabled:opacity-50 ${focusClass}`}>
      <Crop size={16} aria-hidden />{editT('title')}
    </button>}

    <KagemaneMaterials materials={materials} maxMaterials={maxMaterials} disabled={disabled} onAdd={onAddMaterial} onRemove={onRemoveMaterial} optionalPeople={optionalPeople} />
    </div>

    {showDepth && <div className={`${panelClass} px-4 py-2`}>
      <div className="flex min-h-11 items-center justify-between gap-3">
        <span id="kagemane-depth-label" className="text-sm font-medium">{t('enableDepth')}</span>
        <button type="button" role="switch" aria-checked={depthEnabled} aria-labelledby="kagemane-depth-label"
          disabled={disabled} onClick={onToggleDepth}
          className={`relative flex min-h-11 w-11 shrink-0 items-center rounded-lg disabled:cursor-not-allowed disabled:opacity-50 ${focusClass}`}>
          <span className={`relative h-6 w-11 rounded-full transition-colors motion-reduce:transition-none ${depthEnabled ? 'bg-[#4ABDE9]' : 'bg-[#CBD3D9]'}`}>
            <span className={`absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${depthEnabled ? 'translate-x-5' : ''}`} />
          </span>
        </button>
      </div>
    </div>}

    <div className={`${panelClass} px-4 py-2`}>
      <div className="flex min-h-11 items-center justify-between gap-3">
        <span id="kagemane-prompt-label" className="text-sm font-medium">{t('optionalPrompt')}</span>
        <button type="button" role="switch" aria-checked={promptEnabled} aria-labelledby="kagemane-prompt-label" aria-controls="kagemane-prompt-field" disabled={disabled} onClick={onTogglePrompt}
          className={`relative flex min-h-11 w-11 shrink-0 items-center rounded-lg disabled:opacity-50 ${focusClass}`}>
          <span className={`relative h-6 w-11 rounded-full transition-colors motion-reduce:transition-none ${promptEnabled ? 'bg-[#4ABDE9]' : 'bg-[#CBD3D9]'}`}>
            <span className={`absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${promptEnabled ? 'translate-x-5' : ''}`} />
          </span>
        </button>
      </div>
      <div id="kagemane-prompt-field" hidden={!promptEnabled} className="pb-2 pt-1">
        <textarea value={prompt} onChange={(event) => onPromptChange(event.target.value)} maxLength={promptLimit} disabled={disabled} rows={3}
          aria-label={t('prompt')} aria-invalid={promptTooLong || undefined} aria-describedby={promptTooLong ? 'kagemane-prompt-error' : undefined} placeholder={t('promptPlaceholder')}
          className={`block w-full resize-y rounded-xl border border-[#E8ECEF] bg-white px-3 py-2 text-sm leading-6 placeholder:text-[#667680] ${focusClass}`} />
        {promptTooLong && <p id="kagemane-prompt-error" role="alert" className="mt-2 text-xs text-red-600">{t('promptTooLong', { limit: promptLimit })}</p>}
      </div>
    </div>

    <div className={`flex min-h-[60px] items-center justify-between gap-3 px-4 ${panelClass}`}>
      <label htmlFor="kagemane-studio-resolution" className="text-sm font-medium">{t('resolution')}</label>
      <Select id="kagemane-studio-resolution" ariaLabel={t('resolution')} value={resolution} disabled={disabled} variant="brand"
        onChange={(value) => onResolutionChange(value as KagemaneResolution)} className="w-32 shrink-0"
        options={resolutionOptions.map((value) => ({ value, label: value }))} />
    </div>
    {onModelChange && <div className={`${panelClass} px-4 py-2`}>
      <div className="flex min-h-11 items-center justify-between gap-3">
        <label htmlFor="kagemane-studio-model" className="text-sm font-medium">{t('model')}</label>
        <Select id="kagemane-studio-model" ariaLabel={t('model')} value={model ?? 'kling4'} disabled={disabled} variant="brand"
          onChange={(value) => onModelChange(value as 'seedance' | 'heygen' | 'kling4' | 'genjutsu')} className="w-36 shrink-0"
          ariaDescribedBy={modelError || model === 'heygen' ? 'kagemane-model-help' : undefined}
          options={[{ value: 'kling4', label: t('modelKling4') }, { value: 'seedance', label: t('modelSeedance') }, { value: 'heygen', label: t('modelHeygen') }, { value: 'genjutsu', label: t('modelGenjutsu') }]} />
      </div>
      {(model === 'heygen' || modelError) && <p id="kagemane-model-help" role={modelError ? 'alert' : undefined}
        className={`pb-1 text-xs leading-5 ${modelError ? 'text-red-600' : 'text-[#53636D]'}`}>{modelError || t('heygenHelp')}</p>}
      {model === 'genjutsu' && genjutsuFields}
    </div>}
  </div>;
}
